import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { Observable, of, BehaviorSubject } from 'rxjs';

import {
  HttpStatusResult,
  Filtering,
  extractErrors,
  YEAR_PATTERN,
  WITHOUT_EMIT_EVENT,
} from '@jct/core';
import { AppLayoutService, StateFactory } from '@jct/ui';

import {
  HighSchoolService,
  StudentHighSchool,
  ProfileService,
  RegistrationStates,
  RegistrationNavigator,
  RegistrationProfile,
  RegistrationStages,
} from '@jct/api';
import { Gender } from '@jct/localization';

import { RegistrationRowsEditor } from '../../shared-components/registration-rows-editor';

interface TableRow {
  name?: string;
  country?: string;
  city?: string;
  startYear?: number;
  endYear?: number;
}

@Component({
  selector: 'high-school-tab.page',
  templateUrl: './high-school-tab.component.html',
})
export class HighSchoolTabComponent implements OnInit {
  @Output()
  done = new EventEmitter<any>();

  constructor(
    private highSchoolService: HighSchoolService,
    private sf: StateFactory,
    private fb: UntypedFormBuilder,
    private registrationNavigator: RegistrationNavigator,
    private profileService: ProfileService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
    public appLayout: AppLayoutService,
  ) {}

  dataSource = new BehaviorSubject<AbstractControl[]>([]);
  displayColumns: string[] = ['name', 'country', 'city', 'start-year', 'end-year', 'actions'];

  rows: UntypedFormArray = this.fb.array([]);
  form: UntypedFormGroup = this.fb.group({ highSchools: this.rows });

  rowsEditor = new RegistrationRowsEditor(this.rows, this.appLayout, this.host, this.cdr, {
    isFilled: row => !!row.getRawValue()?.name,
    addEmptyRow: () => this.addEmptyRow(),
    removeRow: index => this._removeRowAt(index),
    resetRow: row => this._resetRow(row),
    isRowValid: row => this._isRowValid(row),
  });

  loadState = this.sf.create();
  saveState = this.sf.create();
  showError = false;

  filteredHighSchools: Observable<string[]>;

  get gender(): Gender {
    return this.profileService.profile?.gender;
  }

  async valueChanged(value: string) {
    this.filteredHighSchools = of(await this.filterInput(value));
  }

  async getDataAsync(filtering: Filtering): Promise<string[]> {
    let result = await this.highSchoolService.searchHighSchoolsAsync(filtering);

    if (result instanceof HttpStatusResult) {
      return [];
    }

    return result;
  }

  async filterInput(value: string): Promise<string[]> {
    if (!value) {
      return await this.getDataAsync(new Filtering(100, ''));
    }

    return await this.getDataAsync(new Filtering(100, value));
  }

  maxStartYear = new Date().addYears(-1).getFullYear();
  maxEndYear = new Date().getFullYear();

  get errorList() {
    return extractErrors(this.rows);
  }

  private _setEndYearValidators(row: UntypedFormGroup, startYearValue: number | null) {
    const endYearValidators: ValidatorFn[] = [];

    endYearValidators.push(Validators.max(this.maxEndYear));
    if (startYearValue !== null) {
      endYearValidators.push(Validators.min(startYearValue));
    }

    row.get('endYear').setValidators(endYearValidators);
  }

  private createRow(data?: TableRow) {
    const startYearValue = data ? Math.min(data.startYear, this.maxStartYear) : null;

    const config = {
      name: [data ? data.name : null],
      city: [{ value: data ? data.city : null, disabled: !data }],
      country: [{ value: data ? data.country : null, disabled: !data }],
      startYear: [
        { value: startYearValue, disabled: !data },
        [Validators.required, Validators.pattern(YEAR_PATTERN), Validators.max(this.maxStartYear)],
      ],
      endYear: [
        { value: data ? data.endYear : null, disabled: !data },
        [Validators.min(data?.startYear || 1900), Validators.max(this.maxEndYear)],
      ],
    };

    const row = this.fb.group(config);

    this._setEndYearValidators(row, startYearValue);

    row.get('startYear').valueChanges.subscribe(value => {
      this._setEndYearValidators(row, value);
    });

    row.controls['name'].valueChanges.subscribe(async name => {
      this.filteredHighSchools = of(await this.filterInput(name));
    });

    row.valueChanges.subscribe((data: TableRow) => {
      if (data.name) {
        row.enable(WITHOUT_EMIT_EVENT);

        if (!this.appLayout.isMobile) {
          this.addEmptyRow();
        }
      }
    });

    this.rows.push(row);
  }

  async ngOnInit() {
    await this.loadState.inProcess();

    const result = await this.highSchoolService.getStudentHighSchoolsAsync();

    if (result instanceof HttpStatusResult) {
      if (!result.isEntityNotExists) {
        this.loadState.failed(result);
        return;
      }
    } else {
      result.forEach(data => this.createRow(data));
    }

    this.rowsEditor.initialize();

    this._syncTable();
    await this.loadState.completed();
  }

  addEmptyRow() {
    if (!this.hasEmptyRow) {
      this.createRow();
      this._syncTable();
    }
  }

  private get hasEmptyRow() {
    return this.rows.controls.findIndex(x => x.value.name == null) >= 0;
  }

  private _syncTable() {
    this.dataSource.next(this.rows.controls.slice());
  }

  removeHighSchool(index: number) {
    const row = this.rows.at(index) as UntypedFormGroup;

    if (index >= 0 && this.canRemoveHighSchool(row)) {
      this._removeRowAt(index);
    }
  }

  private _removeRowAt(index: number) {
    this.rows.removeAt(index);
    this._syncTable();

    if (!this.appLayout.isMobile) {
      this.addEmptyRow();
    }

    this.rowsEditor.onRowRemoved(index);
  }

  get returnStudent() {
    return this.profileService.profile?.state == RegistrationStates.ReturnStudent;
  }

  canRemoveHighSchool(row: AbstractControl) {
    return !!row?.getRawValue()?.name && !this.returnStudent;
  }

  rowYearRange(row: AbstractControl) {
    const value = row.getRawValue() as TableRow;

    if (value.startYear && value.endYear) {
      return `${value.startYear}–${value.endYear}`;
    }

    return value.startYear ? `${value.startYear}` : '';
  }

  rowCityCountry(row: AbstractControl) {
    const { city, country } = row.getRawValue() as TableRow;
    const parts = [city, country].filter(x => !!x);

    return parts.join(' | ');
  }

  private _isRowValid(row: UntypedFormGroup) {
    const value = row.getRawValue() as TableRow;

    return (
      !!value.name &&
      !!value.startYear &&
      !row.get('startYear').invalid &&
      !row.get('endYear').invalid
    );
  }

  private _resetRow(row: UntypedFormGroup) {
    row.patchValue(
      {
        name: null,
        city: null,
        country: null,
        startYear: null,
        endYear: null,
      },
      WITHOUT_EMIT_EVENT,
    );

    row.get('city').disable(WITHOUT_EMIT_EVENT);
    row.get('country').disable(WITHOUT_EMIT_EVENT);
    row.get('startYear').disable(WITHOUT_EMIT_EVENT);
    row.get('endYear').disable(WITHOUT_EMIT_EVENT);
    this._setEndYearValidators(row, null);
  }

  get incompleted() {
    let incompleted = this.rows.controls.filter(
      x => (!x.value.name && x.value.startYear) || (x.value.name && !x.value.startYear),
    );

    return incompleted.length > 0;
  }

  get canSubmit() {
    return !this.incompleted && !this.form.invalid;
  }

  async saveAsync() {
    if (this.saveState.isInProcess) {
      return;
    }

    await this.saveState.inProcess();

    this.form.markAllAsTouched();

    if (!this.canSubmit) {
      this.showError = true;
      await this.saveState.completed();
      return;
    }

    let studentHighSchools = this.form.value.highSchools.filter(x => x.name && x.startYear);

    let model = studentHighSchools as StudentHighSchool[];
    let result = await this.highSchoolService.saveStudentHighSchoolsAsync(model);

    if (!result.succeeded) {
      this.saveState.failed(result);
      return;
    }

    this.profileService.update<RegistrationProfile>({
      currentStage: RegistrationStages.תיכונים,
    });

    await this.saveState.completed();
    this.registrationNavigator.navigateToNextStage();
  }
}
